'use client';

import type { Swatch } from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import classNames from 'classnames';
import uniqBy from 'es-toolkit/compat/uniqBy';
import { useState } from 'react';
import { MissingImage } from '../ProductCard/MissingImage';
import { ProductLink } from '../ProductCard/ProductLink';
import { Tooltip } from './Tooltip';

type Props = {
  swatches: Swatch[];
  activeSwatch: string;
  showColorLabel?: boolean;
  swatchSize?: number;
  action?: string;
  /**
   * When given, swatches are buttons that report the pick instead of links
   * to sibling products — e.g. finishes on a configurable KB&F item.
   */
  onSelect?: (swatch: Swatch) => void;
  /**
   * Swatches to look at, not pick: neither links nor buttons, and each one
   * grows on hover so the finish can be seen up close — e.g. the finishes a
   * door style comes in, which are picked in its configurator.
   */
  displayOnly?: boolean;
};

export const SwatchCollection = ({
  swatches,
  activeSwatch,
  showColorLabel = true,
  swatchSize = 48,
  onSelect,
  displayOnly = false,
}: Props) => {
  const t = useTranslations('products');
  const uniqueSwatches = uniqBy(swatches, (s) => s.UPID);
  const selectedSwatch = swatches.find((s) => s.UPID === activeSwatch);
  const [showTooltipIndex, setShowTooltipIndex] = useState<number | null>(null);
  return (
    <div>
      {showColorLabel && (
        <div
          data-cy="product-color"
          className="ui-mb-1 ui-font-semibold ui-text-sm ui-text-gray-500"
        >
          {t('color')}:{' '}
          <span className="ui-font-normal ui-text-black">
            {selectedSwatch?.colorName}
          </span>
        </div>
      )}

      <ul
        data-cy="swatch-list"
        className="ui-flex ui-flex-wrap ui-gap-0.5 !ui-m-0 !ui-mt-2 ui-p-0 !ui-list-none"
      >
        {uniqueSwatches.map((swatch, index) => {
          const sku = swatch.sku.toLowerCase();
          const isActive = swatch.UPID === activeSwatch;
          const imageURL = swatch.imageURL;
          const colorName = swatch.colorName;
          const tile = (
            <>
              {!imageURL && (
                <MissingImage
                  style={{
                    height: `${swatchSize}px`,
                    width: `${swatchSize}px`,
                  }}
                  className={classNames(
                    'ui-shrink-0 ui-border-solid ui-border-2 ui-border-transparent ui-rounded-md ui-fill-gray-400/50',
                    {
                      '!ui-border-[var(--cta-color,#030712)]': isActive,
                    }
                  )}
                />
              )}
              {imageURL && (
                <img
                  style={{
                    height: `${swatchSize}px`,
                    width: `${swatchSize}px`,
                  }}
                  className={classNames(
                    'ui-shrink-0 ui-object-cover ui-object-center ui-border-solid ui-border-2 ui-border-transparent ui-rounded-md',
                    {
                      '!ui-border-[var(--cta-color,#030712)]': isActive,
                    }
                  )}
                  src={
                    imageURL.includes('res.cloudinary.com')
                      ? imageURL.replace(
                          'https://images.floorforce.com/Textures/',
                          ''
                        )
                      : imageURL
                  }
                  // Display-only tiles carry the name themselves.
                  alt={displayOnly ? '' : colorName}
                  height={swatchSize}
                  width={swatchSize}
                />
              )}
            </>
          );
          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <li
              onMouseLeave={() => setShowTooltipIndex(null)}
              onMouseEnter={() => setShowTooltipIndex(index)}
              key={`${colorName}-${sku}`}
              title={colorName}
              className="ui-relative"
            >
              <Tooltip tip={colorName} show={index === showTooltipIndex} />
              {displayOnly ? (
                // Grows downwards, so the name tooltip above stays readable.
                // Focus shows the same preview and name as hover, so keyboard
                // users get them too; the label names a swatch without image.
                <span
                  data-testid="swatch-tile"
                  role="img"
                  aria-label={colorName}
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: focus is how keyboard users reach the name and enlarged swatch that hover shows
                  tabIndex={0}
                  onFocus={() => setShowTooltipIndex(index)}
                  onBlur={() => setShowTooltipIndex(null)}
                  className="ui-relative ui-block ui-leading-none ui-origin-top ui-transition-transform ui-duration-150 hover:ui-z-10 hover:ui-scale-[2] focus-visible:ui-z-10 focus-visible:ui-scale-[2]"
                >
                  {tile}
                </span>
              ) : onSelect ? (
                <button
                  type="button"
                  aria-pressed={isActive}
                  aria-label={colorName}
                  onClick={() => onSelect(swatch)}
                  className="ui-block ui-leading-none ui-p-0 ui-bg-transparent ui-border-0 ui-cursor-pointer"
                >
                  {tile}
                </button>
              ) : (
                <ProductLink
                  product={{ slug: swatch.slug, sku }}
                  className="ui-block ui-leading-none"
                >
                  {tile}
                </ProductLink>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
